/**
 * Poseidon dashboard — system event log (§3.2 A) and the audit/event view of
 * §3.2 G.
 *
 * Every row carries its actor and a `redacted` flag, because §11 requires that
 * secrets and account identifiers be stripped before an operator-facing log is
 * produced — and an operator needs to know that redaction happened.
 */
import type { SystemEventRow } from '../api/types';
import { SEVERITY_LABELS, SYSTEM_EVENT_KIND_LABELS, toneForSeverity } from '../lib/labels';
import { formatDateTimeWithZone } from '../lib/format';
import { DataTable } from './DataTable';
import type { TableColumn } from './DataTable';
import { Badge, Panel } from './primitives';
import type { Tone } from '../lib/staleness';

export function SystemEventList({
  events,
  title = 'System events',
  subtitle,
  mock = false,
  maxRows,
  onRowClick,
}: {
  events: readonly SystemEventRow[];
  title?: string;
  subtitle?: string;
  mock?: boolean;
  maxRows?: number;
  onRowClick?: (event: SystemEventRow) => void;
}): JSX.Element {
  const rows = maxRows === undefined ? events : events.slice(0, maxRows);

  const columns: readonly TableColumn<SystemEventRow, 'time' | 'kind' | 'severity' | 'summary' | 'actor'>[] = [
    { key: 'time', header: 'When', width: '150px', sortValue: (row) => row.occurred_at, render: (row) => <span className="text-mono">{formatDateTimeWithZone(row.occurred_at)}</span> },
    { key: 'severity', header: 'Severity', sortValue: (row) => row.severity, render: (row) => <Badge tone={toneForSeverity(row.severity)}>{SEVERITY_LABELS[row.severity]}</Badge> },
    { key: 'kind', header: 'Kind', sortValue: (row) => row.kind, render: (row) => SYSTEM_EVENT_KIND_LABELS[row.kind] },
    {
      key: 'summary',
      header: 'Event',
      render: (row) => (
        <>
          <div>{row.summary}</div>
          {row.detail !== null && (
            <div className="text-faint" style={{ fontSize: 11.5 }}>
              {row.detail}
            </div>
          )}
          {row.redacted && (
            <span className="text-faint" style={{ fontSize: 11 }} title="§11: secrets, keys and account identifiers are stripped from logs before they reach the dashboard.">
              redacted payload
            </span>
          )}
        </>
      ),
    },
    { key: 'actor', header: 'Actor', sortValue: (row) => row.actor, render: (row) => <span className="text-mono">{row.actor}</span> },
  ];

  const warnCount = events.filter((event) => (event.severity === 'warning' || event.severity === 'error' || event.severity === 'critical')).length;

  return (
    <Panel
      title={title}
      subtitle={subtitle}
      mock={mock}
      flush
      footnote={
        <>
          {events.length} events loaded · {warnCount} at warning or above. MT5 connection changes, data-quality gate failures, risk
          events, API error summaries and manual actions all land here (§3.2 A).
        </>
      }
    >
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.event_id}
        initialSort={{ key: 'time', direction: 'desc' }}
        onRowClick={onRowClick}
        rowTone={(row): Tone | null => (row.severity === 'critical' || row.severity === 'error' ? 'danger' : row.severity === 'warning' ? 'warn' : null)}
        emptyTitle="No system events in this window"
        emptyMessage="An empty event log is itself information: it means nothing was recorded, not that nothing happened."
      />
    </Panel>
  );
}

/** §3.2 G: bridge / reconciliation summary shown on the Operations page. */
export function ReconciliationSummary({
  reconciliation,
}: {
  reconciliation: {
    performed_at: string | null;
    ok: boolean | null;
    positions_checked: number;
    orders_checked: number;
    mismatches: { kind: string; detail: string; ticket: string | null }[];
  };
}): JSX.Element {
  return (
    <div className="grid" style={{ gap: 8 }}>
      <div className="data-clock">
        <span className="text-faint">last reconciliation</span>
        <span className="text-mono">{reconciliation.performed_at === null ? 'never' : formatDateTimeWithZone(reconciliation.performed_at)}</span>
        <Badge tone={reconciliation.ok === null ? 'muted' : reconciliation.ok ? 'ok' : 'danger'}>
          {reconciliation.ok === null ? 'unknown' : reconciliation.ok ? 'clean' : 'mismatch'}
        </Badge>
        <span className="text-faint">
          {reconciliation.positions_checked} positions · {reconciliation.orders_checked} orders checked
        </span>
      </div>
      {reconciliation.mismatches.length === 0 ? (
        <span className="text-ok" style={{ fontSize: 12.5 }}>
          No account/ledger mismatches in the last pass.
        </span>
      ) : (
        reconciliation.mismatches.map((mismatch) => (
          <div key={`${mismatch.kind}-${mismatch.ticket ?? 'none'}`} className="halt-strip halt-strip--soft" style={{ alignItems: 'flex-start' }}>
            <div>
              <strong>{mismatch.kind.replace(/_/g, ' ')}</strong>
              <div style={{ fontSize: 12.5 }}>{mismatch.detail}</div>
              {mismatch.ticket !== null && <div className="text-mono" style={{ fontSize: 11 }}>ticket {mismatch.ticket}</div>}
            </div>
          </div>
        ))
      )}
      <span className="text-faint" style={{ fontSize: 11.5 }}>
        §7.1: reconciliation health must be verified before any submission. A mismatch blocks entries until it is resolved.
      </span>
    </div>
  );
}
